//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';

const buttonTheme = theme.echo.components.button;

export interface IButtonStyle {
  color: string;
  backgroundColor: string;
  borderColor: string;
  textDecoration?: string;
}

export interface IButtonStateStyles {
  fixed: {
    fontWeight: 'normal' | 'bold';
    borderShape: 'pill' | 'circle' | 'square' | 'default' | 'none';
    borderThickness: 'thin' | 'thick';
  };
  standard: IButtonStyle;
  hover: IButtonStyle;
  active: IButtonStyle;
  disabled: IButtonStyle;
  isInlineWithText?: boolean;
}

const globalDisabledStyle: IButtonStyle = {
  color: buttonTheme.disabled.text,
  backgroundColor: buttonTheme.disabled.background,
  borderColor: buttonTheme.disabled.border,
};

export const PrimaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: buttonTheme.primary.text.default,
    backgroundColor: buttonTheme.primary.background.default,
    borderColor: buttonTheme.primary.border.default,
  },
  hover: {
    color: buttonTheme.primary.text.hover,
    backgroundColor: buttonTheme.primary.background.hover,
    borderColor: buttonTheme.primary.border.hover,
  },
  active: {
    color: buttonTheme.primary.text.active,
    backgroundColor: buttonTheme.primary.background.active,
    borderColor: buttonTheme.primary.border.active,
  },
  disabled: globalDisabledStyle,
};

export const SecondaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: buttonTheme.secondary.text.default,
    backgroundColor: buttonTheme.secondary.background.default,
    borderColor: buttonTheme.secondary.border.default,
  },
  hover: {
    color: buttonTheme.secondary.text.hover,
    backgroundColor: buttonTheme.secondary.background.hover,
    borderColor: buttonTheme.secondary.border.hover,
  },
  active: {
    color: buttonTheme.secondary.text.active,
    backgroundColor: buttonTheme.secondary.background.active,
    borderColor: buttonTheme.secondary.border.active,
  },
  disabled: globalDisabledStyle,
};

export const TertiaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: buttonTheme.tertiary.text.default,
    backgroundColor: buttonTheme.tertiary.background.default,
    borderColor: buttonTheme.tertiary.border.default,
  },
  hover: {
    color: buttonTheme.tertiary.text.hover,
    backgroundColor: buttonTheme.tertiary.background.hover,
    borderColor: buttonTheme.tertiary.border.hover,
  },
  active: {
    color: buttonTheme.tertiary.text.active,
    backgroundColor: buttonTheme.tertiary.background.active,
    borderColor: buttonTheme.tertiary.border.active,
  },
  disabled: globalDisabledStyle,
};

export const DefaultButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'default',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.default.text.default,
    backgroundColor: buttonTheme.default.background.default,
    borderColor: buttonTheme.default.border.default,
  },
  hover: {
    color: buttonTheme.default.text.hover,
    backgroundColor: buttonTheme.default.background.hover,
    borderColor: buttonTheme.default.border.hover,
  },
  active: {
    color: buttonTheme.default.text.active,
    backgroundColor: buttonTheme.default.background.active,
    borderColor: buttonTheme.default.border.active,
  },
  disabled: globalDisabledStyle,
};

export const DefaultPlaceholderButtonStyles: IButtonStateStyles = {
  ...DefaultButtonStyles,
  standard: {
    ...DefaultButtonStyles.standard,
  },
};

/**
 * The DefaultError styles are exclusively used in the DropdownButton atom to show an error state
 * for dropdowns used within a form. They are just stored here to help ensure consistency, and
 * should not be used without explicit direction from design.
 */
export const DefaultErrorButtonStyles: IButtonStateStyles = {
  ...DefaultButtonStyles,
  standard: {
    ...DefaultButtonStyles.standard,
    backgroundColor: buttonTheme.dropdown.background.error,
    borderColor: buttonTheme.dropdown.border.error,
  },
};

/**
 * The DefaultError styles are exclusively used in the DropdownButton atom to show an error state
 * for dropdowns used within a form. They are just stored here to help ensure consistency, and
 * should not be used without explicit direction from design.
 */
export const DefaultErrorPlaceholderButtonStyles: IButtonStateStyles = {
  ...DefaultPlaceholderButtonStyles,
  standard: {
    ...DefaultPlaceholderButtonStyles.standard,
    backgroundColor: buttonTheme.dropdown.background.error,
    borderColor: buttonTheme.dropdown.border.error,
  },
};

export const DangerButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: buttonTheme.danger.text.default,
    backgroundColor: buttonTheme.danger.background.default,
    borderColor: buttonTheme.danger.border.default,
  },
  hover: {
    color: buttonTheme.danger.text.hover,
    backgroundColor: buttonTheme.danger.background.hover,
    borderColor: buttonTheme.danger.border.hover,
  },
  active: {
    color: buttonTheme.danger.text.active,
    backgroundColor: buttonTheme.danger.background.active,
    borderColor: buttonTheme.danger.border.active,
  },
  disabled: globalDisabledStyle,
};

export const FlatPrimaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.flatPrimary.text.default,
    backgroundColor: buttonTheme.flatPrimary.background.default,
    borderColor: buttonTheme.flatPrimary.border.default,
  },
  hover: {
    color: buttonTheme.flatPrimary.text.hover,
    backgroundColor: buttonTheme.flatPrimary.background.hover,
    borderColor: buttonTheme.flatPrimary.border.hover,
  },
  active: {
    color: buttonTheme.flatPrimary.text.active,
    backgroundColor: buttonTheme.flatPrimary.background.active,
    borderColor: buttonTheme.flatPrimary.border.active,
  },
  disabled: globalDisabledStyle,
};

export const FlatDefaultButtonStyles: IButtonStateStyles = {
  ...FlatPrimaryButtonStyles,
  standard: {
    ...FlatPrimaryButtonStyles.standard,
    color: buttonTheme.flatDefault.text.default,
  },
};

export const FlatDefaultIconButtonStyles: IButtonStateStyles = {
  ...FlatDefaultButtonStyles,
  fixed: {
    ...FlatDefaultButtonStyles.fixed,
    borderShape: 'circle',
  },
};

export const IconButtonStyles: IButtonStateStyles = {
  ...TertiaryButtonStyles,
  fixed: {
    ...TertiaryButtonStyles.fixed,
    borderShape: 'circle',
  },
};

export const DefaultIconButtonStyles: IButtonStateStyles = {
  ...DefaultButtonStyles,
  fixed: {
    ...DefaultButtonStyles.fixed,
    borderShape: 'square',
  },
};

export const OverlayButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'default',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.overlay.text.default,
    backgroundColor: buttonTheme.overlay.background.default,
    borderColor: buttonTheme.overlay.border.default,
  },
  hover: {
    color: buttonTheme.overlay.text.hover,
    backgroundColor: buttonTheme.overlay.background.hover,
    borderColor: buttonTheme.overlay.border.hover,
  },
  active: {
    color: buttonTheme.overlay.text.active,
    backgroundColor: buttonTheme.overlay.background.active,
    borderColor: buttonTheme.overlay.border.active,
  },
  disabled: globalDisabledStyle,
};

export const OverlayIconButtonStyles: IButtonStateStyles = {
  ...OverlayButtonStyles,
  fixed: {
    fontWeight: 'bold',
    borderShape: 'circle',
    borderThickness: 'thin',
  },
  standard: {
    ...OverlayButtonStyles.standard,
    borderColor: 'transparent',
  },
};

export const OverlayIconMediaButtonStyles: IButtonStateStyles = {
  ...OverlayIconButtonStyles,
  standard: {
    ...OverlayIconButtonStyles.standard,
  },
  active: {
    ...OverlayIconButtonStyles.active,
    backgroundColor: buttonTheme.overlayIconMedia.background.active,
    borderColor: 'transparent',
  },
  hover: {
    ...OverlayIconButtonStyles.hover,
    borderColor: 'transparent',
  },
};

export const PrimaryLinkButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.linkPrimary.text.default,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: buttonTheme.linkPrimary.text.hover,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  active: {
    color: buttonTheme.linkPrimary.text.active,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  isInlineWithText: true,
};

export const BodyPrimaryLinkButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.linkBody.text.default,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: buttonTheme.linkBody.text.hover,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  active: {
    color: buttonTheme.linkBody.text.active,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  isInlineWithText: true,
};

export const ActionSheetButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.actionSheet.text.default,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: buttonTheme.actionSheet.text.hover,
    backgroundColor: buttonTheme.actionSheet.background.hover,
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  active: {
    color: buttonTheme.actionSheet.text.active,
    backgroundColor: buttonTheme.actionSheet.background.active,
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  isInlineWithText: true,
};

export const InvisibleButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: buttonTheme.invisible.text.default,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: buttonTheme.invisible.text.hover,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  active: {
    color: buttonTheme.invisible.text.active,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
};

export const InvisibleButtonDeprecatedStyles: IButtonStateStyles = {
  ...InvisibleButtonStyles,
  standard: {
    ...InvisibleButtonStyles.standard,
    color: theme.echo.color.text.onLight.subtle,
  },
  hover: {
    ...InvisibleButtonStyles.hover,
    color: theme.echo.color.text.onLight.default,
  },
};
